// The shelves: areas and the stages inside them.
//
// Split from the content page rather than bolted onto it. Lessons are
// edited constantly; structure is edited a handful of times a year, and
// mixing a rarely-used destructive control into the screen someone uses
// daily is how an area gets deleted by accident.
//
// Everything here is ordered, and order is a teaching decision — it is
// the sequence a learner meets whole tracks and stages in — so it is
// editable rather than inferred.
import { useEffect, useState } from "react";
import {
  ChevronDown,
  ChevronRight,
  Layers,
  Loader2,
  Pencil,
  Plus,
  Trash2,
  X,
} from "lucide-react";
import Layout from "@/components/Layout";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  useAcademyAreas,
  useCreateArea,
  useCreateStage,
  useDeleteArea,
  useDeleteStage,
  useUpdateArea,
  useUpdateStage,
} from "@/hooks/api/useAcademy";
import type {
  AcademyAdminArea,
  AcademyAdminStage,
} from "@/api/services/academy.service";
import { toast } from "react-toastify";

function errText(e: unknown, fallback: string): string {
  const msg = (e as { response?: { data?: { message?: unknown } } })?.response
    ?.data?.message;
  if (Array.isArray(msg)) return msg.join("\n");
  if (typeof msg === "string") return msg;
  return e instanceof Error ? e.message : fallback;
}

function slugify(v: string): string {
  return v
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

export default function AcademyStructurePage() {
  const { data: areas, isLoading } = useAcademyAreas();
  const [addingArea, setAddingArea] = useState(false);

  return (
    <Layout>
      <div className="space-y-6">
        <div className="rounded-2xl bg-gradient-to-br from-sky-600 via-indigo-700 to-violet-800 p-6 text-white shadow-lg">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="rounded-xl bg-white/15 p-2.5 backdrop-blur">
                <Layers className="h-6 w-6" />
              </div>
              <div>
                <h1 className="text-2xl font-bold tracking-tight">
                  Academy Structure
                </h1>
                <p className="text-sm text-white/70">
                  Areas and the stages inside them. Lessons are filed here.
                </p>
              </div>
            </div>
            <Button
              className="cursor-pointer gap-1.5 bg-white text-indigo-700 hover:bg-white/90"
              onClick={() => setAddingArea((v) => !v)}
            >
              {addingArea ? (
                <>
                  <X className="h-4 w-4" /> Cancel
                </>
              ) : (
                <>
                  <Plus className="h-4 w-4" /> New area
                </>
              )}
            </Button>
          </div>
        </div>

        {addingArea && <NewAreaForm onDone={() => setAddingArea(false)} />}

        {isLoading ? (
          <div className="flex items-center justify-center gap-2 py-20 text-muted-foreground">
            <Loader2 className="h-5 w-5 animate-spin" />
            Loading structure…
          </div>
        ) : (
          <div className="space-y-3">
            {(areas ?? []).map((area) => (
              <AreaCard key={area.key} area={area} />
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
}

function NewAreaForm({ onDone }: { onDone: () => void }) {
  const create = useCreateArea();
  const [name, setName] = useState("");
  const [key, setKey] = useState("");
  const [keyTouched, setKeyTouched] = useState(false);
  const [sub, setSub] = useState("");
  const [icon, setIcon] = useState("book");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!keyTouched) setKey(slugify(name));
  }, [name, keyTouched]);

  const submit = () => {
    setError(null);
    create.mutate(
      { key: key.trim(), name: name.trim(), sub: sub.trim(), icon: icon.trim() },
      {
        onSuccess: () => {
          toast.success("Area created");
          onDone();
        },
        onError: (e: unknown) => setError(errText(e, "Could not create area")),
      }
    );
  };

  return (
    <div className="space-y-4 rounded-xl border border-indigo-500/30 bg-indigo-500/5 p-4">
      <div className="grid gap-4 sm:grid-cols-2">
        <div className="space-y-1.5">
          <Label>Name</Label>
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Set Pieces"
          />
        </div>
        <div className="space-y-1.5">
          <Label>Key</Label>
          <Input
            value={key}
            onChange={(e) => {
              setKeyTouched(true);
              setKey(e.target.value);
            }}
            placeholder="set-pieces"
            className="font-mono text-sm"
          />
          <p className="text-[11px] text-muted-foreground">
            Permanent — content files reference it.
          </p>
        </div>
        <div className="space-y-1.5">
          <Label>Subtitle</Label>
          <Input
            value={sub}
            onChange={(e) => setSub(e.target.value)}
            placeholder="Corners, free kicks and throw-ins"
          />
        </div>
        <div className="space-y-1.5">
          <Label>Icon</Label>
          <Input
            value={icon}
            onChange={(e) => setIcon(e.target.value)}
            placeholder="book"
            className="font-mono text-sm"
          />
          <p className="text-[11px] text-muted-foreground">
            An Ionicons name — the mobile app renders it.
          </p>
        </div>
      </div>

      {error && (
        <div className="whitespace-pre-wrap rounded border border-red-500/30 bg-red-500/10 p-2.5 text-xs text-red-600">
          {error}
        </div>
      )}

      <Button
        className="cursor-pointer gap-1.5"
        disabled={!name.trim() || !key.trim() || create.isPending}
        onClick={submit}
      >
        {create.isPending ? (
          <Loader2 className="h-4 w-4 animate-spin" />
        ) : (
          <Plus className="h-4 w-4" />
        )}
        Create area
      </Button>
    </div>
  );
}

function AreaCard({ area }: { area: AcademyAdminArea }) {
  const [open, setOpen] = useState(true);
  const [editing, setEditing] = useState(false);
  const [addingStage, setAddingStage] = useState(false);

  const update = useUpdateArea();
  const remove = useDeleteArea();

  const [name, setName] = useState(area.name);
  const [sub, setSub] = useState(area.sub);
  const [icon, setIcon] = useState(area.icon);
  const [order, setOrder] = useState(String(area.order));

  useEffect(() => {
    setName(area.name);
    setSub(area.sub);
    setIcon(area.icon);
    setOrder(String(area.order));
  }, [area]);

  const lessons = area.stages.reduce((s, st) => s + st.conceptCount, 0);

  const save = () => {
    update.mutate(
      {
        key: area.key,
        data: { name, sub, icon, order: Number(order) || 0 },
      },
      {
        onSuccess: () => {
          toast.success("Area updated");
          setEditing(false);
        },
        onError: (e: unknown) => toast.error(errText(e, "Could not update")),
      }
    );
  };

  const onDelete = () => {
    if (!window.confirm(`Delete the area "${area.name}"?`)) return;
    remove.mutate(area.key, {
      onSuccess: () => toast.success("Area deleted"),
      // The server refuses while lessons are filed here and says how
      // many — that sentence is the useful part.
      onError: (e: unknown) => toast.error(errText(e, "Could not delete")),
    });
  };

  return (
    <div className="overflow-hidden rounded-xl border bg-card shadow-sm">
      <div className="flex items-center gap-3 p-4">
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="flex min-w-0 flex-1 cursor-pointer items-center gap-3 text-left"
        >
          {open ? (
            <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" />
          ) : (
            <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
          )}
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <span className="truncate font-semibold">{area.name}</span>
              <code className="shrink-0 rounded bg-muted px-1.5 py-0.5 text-[11px] text-muted-foreground">
                {area.key}
              </code>
              <Badge variant="outline" className="shrink-0 text-[10px]">
                #{area.order}
              </Badge>
            </div>
            <p className="truncate text-xs text-muted-foreground">{area.sub}</p>
          </div>
        </button>

        <span className="shrink-0 text-xs text-muted-foreground">
          {area.stages.length} stage{area.stages.length === 1 ? "" : "s"} ·{" "}
          {lessons} lesson{lessons === 1 ? "" : "s"}
        </span>

        <Button
          size="sm"
          variant="ghost"
          className="cursor-pointer gap-1 text-xs"
          onClick={() => setEditing((v) => !v)}
        >
          {editing ? <X className="h-3.5 w-3.5" /> : <Pencil className="h-3.5 w-3.5" />}
        </Button>
        <Button
          size="sm"
          variant="ghost"
          className="cursor-pointer text-xs text-red-600 hover:text-red-700"
          disabled={remove.isPending}
          onClick={onDelete}
        >
          <Trash2 className="h-3.5 w-3.5" />
        </Button>
      </div>

      {editing && (
        <div className="grid gap-3 border-t bg-muted/20 p-4 sm:grid-cols-4">
          <div className="space-y-1.5">
            <Label className="text-xs">Name</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} />
          </div>
          <div className="space-y-1.5 sm:col-span-2">
            <Label className="text-xs">Subtitle</Label>
            <Input value={sub} onChange={(e) => setSub(e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Icon</Label>
            <Input
              value={icon}
              onChange={(e) => setIcon(e.target.value)}
              className="font-mono text-sm"
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Order</Label>
            <Input
              value={order}
              onChange={(e) => setOrder(e.target.value)}
              inputMode="numeric"
            />
          </div>
          <div className="flex items-end sm:col-span-3">
            <Button
              size="sm"
              className="cursor-pointer"
              disabled={update.isPending}
              onClick={save}
            >
              Save area
            </Button>
          </div>
        </div>
      )}

      {open && (
        <div className="border-t bg-muted/10 p-3">
          <div className="space-y-2">
            {area.stages.map((stage) => (
              <StageRow key={stage.key} areaKey={area.key} stage={stage} />
            ))}
            {area.stages.length === 0 && (
              <p className="py-3 text-center text-xs text-muted-foreground">
                No stages yet. Lessons need one to be filed into.
              </p>
            )}
          </div>

          {addingStage ? (
            <NewStageForm
              areaKey={area.key}
              onDone={() => setAddingStage(false)}
            />
          ) : (
            <Button
              size="sm"
              variant="outline"
              className="mt-2 cursor-pointer gap-1.5"
              onClick={() => setAddingStage(true)}
            >
              <Plus className="h-3.5 w-3.5" />
              Add stage
            </Button>
          )}
        </div>
      )}
    </div>
  );
}

function NewStageForm({
  areaKey,
  onDone,
}: {
  areaKey: string;
  onDone: () => void;
}) {
  const create = useCreateStage();
  const [name, setName] = useState("");
  const [key, setKey] = useState("");
  const [keyTouched, setKeyTouched] = useState(false);
  const [sub, setSub] = useState("");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!keyTouched) setKey(slugify(name));
  }, [name, keyTouched]);

  return (
    <div className="mt-2 space-y-3 rounded-lg border border-indigo-500/30 bg-indigo-500/5 p-3">
      <div className="grid gap-3 sm:grid-cols-3">
        <div className="space-y-1.5">
          <Label className="text-xs">Name</Label>
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Defending Shapes"
          />
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs">Key</Label>
          <Input
            value={key}
            onChange={(e) => {
              setKeyTouched(true);
              setKey(e.target.value);
            }}
            placeholder="defending-shapes"
            className="font-mono text-sm"
          />
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs">Subtitle</Label>
          <Input
            value={sub}
            onChange={(e) => setSub(e.target.value)}
            placeholder="How teams stop you"
          />
        </div>
      </div>

      {error && (
        <div className="whitespace-pre-wrap rounded border border-red-500/30 bg-red-500/10 p-2.5 text-xs text-red-600">
          {error}
        </div>
      )}

      <div className="flex gap-2">
        <Button
          size="sm"
          className="cursor-pointer gap-1.5"
          disabled={!name.trim() || !key.trim() || create.isPending}
          onClick={() => {
            setError(null);
            create.mutate(
              {
                areaKey,
                data: { key: key.trim(), name: name.trim(), sub: sub.trim() },
              },
              {
                onSuccess: () => {
                  toast.success("Stage created");
                  onDone();
                },
                onError: (e: unknown) =>
                  setError(errText(e, "Could not create stage")),
              }
            );
          }}
        >
          {create.isPending ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : (
            <Plus className="h-3.5 w-3.5" />
          )}
          Create stage
        </Button>
        <Button
          size="sm"
          variant="ghost"
          className="cursor-pointer"
          onClick={onDone}
        >
          Cancel
        </Button>
      </div>
    </div>
  );
}

function StageRow({
  areaKey,
  stage,
}: {
  areaKey: string;
  stage: AcademyAdminStage;
}) {
  const [editing, setEditing] = useState(false);
  const update = useUpdateStage();
  const remove = useDeleteStage();

  const [name, setName] = useState(stage.name);
  const [sub, setSub] = useState(stage.sub);
  const [order, setOrder] = useState(String(stage.order));

  useEffect(() => {
    setName(stage.name);
    setSub(stage.sub);
    setOrder(String(stage.order));
  }, [stage]);

  return (
    <div className="rounded-lg border bg-background p-3">
      <div className="flex items-center gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className="truncate text-sm font-medium">{stage.name}</span>
            <code className="shrink-0 rounded bg-muted px-1.5 py-0.5 text-[10px] text-muted-foreground">
              {stage.key}
            </code>
            <Badge variant="outline" className="shrink-0 text-[10px]">
              #{stage.order}
            </Badge>
          </div>
          <p className="truncate text-xs text-muted-foreground">{stage.sub}</p>
        </div>

        <span className="shrink-0 text-xs text-muted-foreground">
          {stage.publishedConceptCount}/{stage.conceptCount} live
        </span>

        <Button
          size="sm"
          variant="ghost"
          className="cursor-pointer"
          onClick={() => setEditing((v) => !v)}
        >
          {editing ? <X className="h-3.5 w-3.5" /> : <Pencil className="h-3.5 w-3.5" />}
        </Button>
        <Button
          size="sm"
          variant="ghost"
          className="cursor-pointer text-red-600 hover:text-red-700"
          disabled={remove.isPending}
          onClick={() => {
            if (!window.confirm(`Delete the stage "${stage.name}"?`)) return;
            remove.mutate(
              { areaKey, stageKey: stage.key },
              {
                onSuccess: () => toast.success("Stage deleted"),
                onError: (e: unknown) =>
                  toast.error(errText(e, "Could not delete")),
              }
            );
          }}
        >
          <Trash2 className="h-3.5 w-3.5" />
        </Button>
      </div>

      {editing && (
        <div className="mt-3 grid gap-3 border-t pt-3 sm:grid-cols-4">
          <div className="space-y-1.5">
            <Label className="text-xs">Name</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} />
          </div>
          <div className="space-y-1.5 sm:col-span-2">
            <Label className="text-xs">Subtitle</Label>
            <Input value={sub} onChange={(e) => setSub(e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Order</Label>
            <Input
              value={order}
              onChange={(e) => setOrder(e.target.value)}
              inputMode="numeric"
            />
          </div>
          <div className="sm:col-span-4">
            <Button
              size="sm"
              className="cursor-pointer"
              disabled={update.isPending}
              onClick={() =>
                update.mutate(
                  {
                    areaKey,
                    stageKey: stage.key,
                    data: { name, sub, order: Number(order) || 0 },
                  },
                  {
                    onSuccess: () => {
                      toast.success("Stage updated");
                      setEditing(false);
                    },
                    onError: (e: unknown) =>
                      toast.error(errText(e, "Could not update")),
                  }
                )
              }
            >
              Save stage
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
